import { FormFieldSet } from "@progress/kendo-react-form";
import { useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ContentLayout,
  ContentHeader,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
  ContentHeaderBackNavigation,
} from "@excent-one/react/layout/content";
import { FormLayout, FormSection, SearchFormLayout } from "@excent-one/react";
import { DataGrid, DataGridColumnProps } from "@excent-one/react/data-grid";
import {
  ControlledDatePicker,
  ControlledInput,
  ControlledComboBox,
} from "@excent-one/react/form/inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-all";
import { notification } from "@excent-one/react/notification";
import {
  saveAllStatusConcernsIssue,
  searchConcernsIssues,
} from "@phrmpc/infrastructure/phrmpc/api/concerns-issues";
import { getAllConcernStatuses } from "@phrmpc/infrastructure/phrmpc/api/concern-statuses";
import { ConcernsIssueSummaryDto } from "@phrmpc/infrastructure/phrmpc/models";
import { FilterLogic, FilterOperator } from "@phrmpc/shared";
import { DateTime } from "luxon";
import {
  getCoordinators,
  getCoordinatorMembers,
  getMyDetails,
} from "@phrmpc/infrastructure/phrmpc";
import { usePagedGrid } from "@phrmpc/shared/hooks/usePagedGrid";
import { dialogBox } from "@excent-one/react/dialog-box";
import { useNavigate } from "@tanstack/react-router";
import { useAuthContext } from "@excent-one/auth";

const baseDefaultSearchValues: ConcernsIssueSummaryDto = {
  concernDate: undefined,
  memberName: "",
  coordinatorId: undefined,
  coordinatorName: "",
  statusId: undefined,
};

const buildFilter = (values: ConcernsIssueSummaryDto) => {
  const filters: { field: string; operator: number; value: unknown }[] = [];
  if (values.concernDate) {
    filters.push({
      field: "concernDate",
      operator: FilterOperator.Equals,
      value: values.concernDate,
    });
  }
  if (values.memberName) {
    filters.push({
      field: "memberName",
      operator: FilterOperator.Contains,
      value: values.memberName,
    });
  }
  if (values.coordinatorId != null) {
    filters.push({
      field: "coordinatorId",
      operator: FilterOperator.Equals,
      value: values.coordinatorId,
    });
  }
  if (values.statusId != null) {
    filters.push({
      field: "statusId",
      operator: FilterOperator.Equals,
      value: values.statusId,
    });
  }
  if (filters.length === 0) return null;
  return { logic: FilterLogic.And, filters } as const;
};

const ConcernsIssuesSearchForm = () => {
  const [{ currentUser }, { isInAnyRole }] = useAuthContext();
  const isUserAreaHead = isInAnyRole("Area Head");
  const isUserCoordinator = isInAnyRole("Coordinator");

  const navigate = useNavigate();

  const { data: statusOptionsData } = useQuery({
    queryKey: ["concern-statuses"],
    queryFn: () => getAllConcernStatuses(),
  });
  const statusOptions = statusOptionsData ?? [];

  const { data: myDetails } = useQuery({
    queryKey: ["my-details", currentUser?.id],
    queryFn: () => getMyDetails(),
  });

  const { data: coordinators } = useQuery({
    queryKey: ["coordinator-members", myDetails?.id],
    queryFn: async () => {
      const result = await getCoordinatorMembers(myDetails!.id!);
      return result.items ?? [];
    },
    enabled: !isUserCoordinator && Boolean(myDetails?.id),
  });

  const fallbackCoordinators = useQuery({
    queryKey: ["coordinators"],
    queryFn: async () => {
      const result = await getCoordinators({
        payload: { pageNumber: 1, pageSize: 500 },
      });
      return result.items ?? [];
    },
    enabled: !isUserAreaHead && !isUserCoordinator,
  });

  const coordinatorOptions = useMemo(() => {
    if (isUserCoordinator) return [];
    if (isUserAreaHead) return coordinators ?? [];
    return fallbackCoordinators.data ?? [];
  }, [
    isUserCoordinator,
    isUserAreaHead,
    coordinators,
    fallbackCoordinators.data,
  ]);

  const defaultSearchValues: ConcernsIssueSummaryDto = {
    ...baseDefaultSearchValues,
    coordinatorId: isUserCoordinator ? myDetails?.id : undefined,
    coordinatorName: isUserCoordinator ? (myDetails?.fieldName ?? "") : "",
  };

  const [pendingStatus, setPendingStatus] = useState<Record<string, number>>(
    {},
  );

  const { control, getValues, reset } = useForm<ConcernsIssueSummaryDto>({
    defaultValues: defaultSearchValues,
  });

  const [appliedFilters, setAppliedFilters] =
    useState<ConcernsIssueSummaryDto>(defaultSearchValues);
  const { skip, pageSize, pageNumber, pageable, onPageChange, resetPaging } =
    usePagedGrid();

  useEffect(() => {
    if (!isUserCoordinator || !myDetails) return;
    const scopedValues: ConcernsIssueSummaryDto = {
      ...baseDefaultSearchValues,
      coordinatorId: myDetails.id,
      coordinatorName: myDetails.fieldName ?? "",
    };
    reset(scopedValues);
    setAppliedFilters(scopedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserCoordinator, myDetails]);

  const {
    data: pagedResult,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["concerns-issues", appliedFilters, skip, pageSize],
    queryFn: () =>
      searchConcernsIssues({
        payload: {
          pageNumber,
          pageSize,
          filter: buildFilter(appliedFilters) as any,
        },
      } as any),
  });

  const isSearchCriteriaEmpty = (values: ConcernsIssueSummaryDto) =>
    !values.concernDate &&
    !values.memberName &&
    (isUserCoordinator || values.coordinatorId == null) &&
    values.statusId == null;

  const handleSearch = () => {
    const values = getValues();
    if (isSearchCriteriaEmpty(values)) {
      notification.error("Please specify at least one search criterion.");
      return;
    }
    resetPaging();
    setAppliedFilters(values);
  };

  const handleClear = () => {
    const clearValues: ConcernsIssueSummaryDto = {
      ...baseDefaultSearchValues,
      coordinatorId: isUserCoordinator ? myDetails?.id : undefined,
      coordinatorName: isUserCoordinator ? (myDetails?.fieldName ?? "") : "",
    };
    reset(clearValues);
    resetPaging();
    setAppliedFilters(clearValues);
  };

  const gridData = (pagedResult?.items ?? []).map((row) => ({
    ...row,
    statusId: pendingStatus[row.id!] ?? row.statusId,
  }));

  const handleStatusChange = (rowId: string, newStatusId: number) => {
    setPendingStatus((prev) => ({ ...prev, [rowId]: newStatusId }));
  };

  const onApply = () => {
    const changedIds = Object.keys(pendingStatus);
    if (changedIds.length === 0) {
      notification.error("No status changes to apply.");
      return;
    }

    dialogBox.confirm({
      title: "Apply status changes",
      content: <>Are you sure you want to apply these status changes?</>,
      actions: {
        confirm: {
          text: "Yes, apply",
          onClick: async () => {
            const payload = (pagedResult?.items ?? [])
              .filter((row) => changedIds.includes(row.id!))
              .map((row) => ({
                ...row,
                statusId: pendingStatus[row.id!],
              }));

            try {
              await saveAllStatusConcernsIssue({ payload } as any);
              notification.success("Status changes applied successfully.");
              setPendingStatus({});
              refetch();
            } catch (e) {
              console.error(e);
              notification.error(
                "Unable to apply status changes. Please try again.",
              );
            }
          },
        },
        cancel: { text: "No" },
      },
    });
  };

  const handleBack = () => {
    const proceed = () => navigate({ to: "/deployment" });

    if (Object.keys(pendingStatus).length > 0) {
      dialogBox.confirm({
        title: "Leave without saving?",
        content: (
          <>You have unsaved status changes. Are you sure you want to leave?</>
        ),
        actions: {
          confirm: { text: "Yes, leave", onClick: proceed },
          cancel: { text: "No" },
        },
      });
    } else {
      proceed();
    }
  };

  const COLUMNS: DataGridColumnProps<ConcernsIssueSummaryDto>[] = [
    {
      field: "concernDate",
      title: "Date",
      readOnly: true,
      width: "120px",
      cells: {
        data: (props: any) => (
          <td>
            {props.dataItem.concernDate instanceof Date
              ? DateTime.fromJSDate(props.dataItem.concernDate).toFormat(
                  "MM/dd/yyyy",
                )
              : "-"}
          </td>
        ),
      },
    },
    {
      field: "memberName",
      title: "Member Name",
      readOnly: true,
    },
    {
      field: "coordinatorName",
      title: "Coordinator Name",
      readOnly: true,
    },
    { field: "concernsIssueTypeName", title: "Concerns/Issue", readOnly: true },
    {
      field: "statusId",
      title: "Status",
      cells: {
        data: (props: any) => {
          const originalRow = pagedResult?.items?.find(
            (item) => item.id === props.dataItem.id,
          );

          const isCompleted =
            statusOptions.find((s) => s.id === originalRow?.statusId)?.name ===
            "Completed";

          return (
            <td>
              <DropDownList
                data={statusOptions}
                dataItemKey="id"
                textField="name"
                value={
                  statusOptions.find(
                    (option) => option.id === props.dataItem.statusId,
                  ) ?? null
                }
                disabled={isCompleted}
                onChange={(e) =>
                  handleStatusChange(props.dataItem.id, e.value.id)
                }
              />
            </td>
          );
        },
      },
    },
  ];

  return (
    <ContentLayout>
      <ContentHeader>
        <ContentHeaderBackNavigation />
        <ContentHeaderTitle title="Search Concerns and Issues" />
        <ContentHeaderBreadcrumbs />
        <SearchFormLayout
          buttonProps={{
            search: { onClick: handleSearch },
            clear: { onClick: handleClear },
          }}
        >
          <FormFieldSet cols={2}>
            <ControlledDatePicker
              control={control}
              name="concernDate"
              label="Date"
            />
            <ControlledInput
              control={control}
              name="memberName"
              label="Member Name"
              placeholder="Search by member name"
              wrapperProps={{ className: "basic-search-field" }}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key.length > 1) return;
                if (!/^[a-zA-Z\s]$/.test(e.key)) {
                  e.preventDefault();
                }
              }}
            />
            {coordinatorOptions.length ? (
              <ControlledComboBox
                control={control}
                name="coordinatorId"
                label="Coordinator Name"
                data={coordinatorOptions}
                dataItemKey="id"
                textField="fieldName"
              />
            ) : (
              isUserCoordinator && (
                <ControlledInput
                  control={control}
                  name="coordinatorName"
                  label="Coordinator Name"
                  wrapperProps={{ className: "basic-search-field" }}
                  readOnly
                />
              )
            )}
            {statusOptionsData && (
              <ControlledComboBox
                control={control}
                name="statusId"
                label="Status"
                data={statusOptionsData}
                dataItemKey="id"
                textField="name"
              />
            )}
          </FormFieldSet>
        </SearchFormLayout>
      </ContentHeader>

      <FormLayout>
        <FormSection title="Results">
          <DataGrid
            data={gridData}
            dataItemKey="id"
            columns={COLUMNS}
            canAdd={false}
            hideRowCommandToggle
            pageable={pageable}
            skip={skip}
            take={pageSize}
            total={pagedResult?.totalItems ?? 0}
            onPageChange={onPageChange}
            noRecordsMessage="No matching concerns and issues found."
            style={{ maxHeight: "500px" }}
            scrollable="virtual"
            loading={isLoading}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 8,
              marginTop: 8,
            }}
          >
            <Button type="button" onClick={handleBack}>
              Back
            </Button>
            <Button type="button" themeColor="primary" onClick={onApply}>
              Apply
            </Button>
          </div>
        </FormSection>
      </FormLayout>
    </ContentLayout>
  );
};

export default ConcernsIssuesSearchForm;
